import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import ExpandableDataTable from "components/admin/ExpandableDataTable";
import StatusBadge from "components/admin/StatusBadge";
import Widget from "components/widget/Widget";
import { BUTTON, DetailActions, DetailField, DetailGrid, DetailSection, IdValue } from "components/admin/DetailPanel";
import { apiGet } from "lib/apiClient";
import { formatKes } from "lib/format";
import { MdCheckCircle, MdSyncProblem, MdReportProblem, MdHelpOutline } from "react-icons/md";

const columnHelper = createColumnHelper();

const OUTCOME_TONE = {
  MATCHED: "green",
  STATUS_MISMATCH: "red",
  AMOUNT_MISMATCH: "red",
  UNCONFIRMED: "amber",
};

const OUTCOME_LABEL = {
  MATCHED: "Matched",
  STATUS_MISMATCH: "Status mismatch",
  AMOUNT_MISMATCH: "Amount mismatch",
  UNCONFIRMED: "Not yet confirmed",
};

const PAYMENT_STATUS_LABEL = {
  SUCCEEDED: "Completed",
  FAILED: "Failed",
  PENDING: "Pending",
};

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function ReconciliationPage() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(isoDaysAgo(7));
  const [endDate, setEndDate] = useState(today());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiGet(`/portal/reconciliation?startDate=${startDate}&endDate=${endDate}`)
      .then(setReport)
      .catch((err) => setError(err.message || "Could not build the reconciliation report."))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = report?.counts || {};
  const problems = (counts.STATUS_MISMATCH || 0) + (counts.AMOUNT_MISMATCH || 0);

  const money = (value, currency) => (value == null ? "—" : formatKes(value, currency || "KES"));

  const columns = [
    columnHelper.accessor("outcome", {
      header: () => <p>OUTCOME</p>,
      cell: (info) => <StatusBadge label={OUTCOME_LABEL[info.getValue()] || info.getValue()} tone={OUTCOME_TONE[info.getValue()] || "gray"} />,
    }),
    columnHelper.accessor("kind", {
      header: () => <p>TYPE</p>,
      cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{info.getValue()}</p>,
    }),
    columnHelper.accessor("paymentAmount", {
      header: () => <p>PAYMENT</p>,
      cell: (info) => <p className="num text-sm text-navy-700 dark:text-white">{money(info.getValue(), info.row.original.currency)}</p>,
    }),
    columnHelper.accessor("amount", {
      header: () => <p>RECORDED</p>,
      cell: (info) => <p className="num text-sm text-navy-700 dark:text-white">{money(info.getValue(), info.row.original.currency)}</p>,
    }),
    columnHelper.accessor("occurredAt", {
      header: () => <p>WHEN</p>,
      cell: (info) => (
        <p className="whitespace-nowrap text-sm text-navy-700 dark:text-white">
          {info.getValue() ? new Date(info.getValue()).toLocaleString() : "—"}
        </p>
      ),
    }),
  ];

  function renderLine(line) {
    return (
      <div>
        <DetailSection title="What disagrees">
          <p className="text-sm text-navy-700 dark:text-white">{line.detail}</p>
        </DetailSection>

        <DetailSection title="Payment and record">
          <DetailGrid>
            <DetailField label="Payment reference">
              <IdValue value={line.reference} />
            </DetailField>
            <DetailField label="Payment status">{PAYMENT_STATUS_LABEL[line.paymentStatus] || "Not found"}</DetailField>
            <DetailField label="Recorded status">{line.status || "—"}</DetailField>
            <DetailField label="Payment amount">{money(line.paymentAmount, line.currency)}</DetailField>
            <DetailField label="Recorded amount">{money(line.amount, line.currency)}</DetailField>
          </DetailGrid>
        </DetailSection>

        <DetailActions>
          <button type="button" className={BUTTON.outline} onClick={() => navigate(`/portal/money-timeline`)}>
            Money timeline
          </button>
        </DetailActions>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <Widget icon={<MdCheckCircle className="h-6 w-6" />} title="Matched" subtitle={String(counts.MATCHED ?? 0)} />
        <Widget icon={<MdReportProblem className="h-6 w-6" />} title="Needs attention" subtitle={String(problems)} />
        <Widget
          icon={<MdSyncProblem className="h-6 w-6" />}
          title="Payments completed"
          subtitle={report ? formatKes(report.totals.confirmedAmount, "KES") : "…"}
        />
        <Widget
          icon={<MdHelpOutline className="h-6 w-6" />}
          title="Difference vs recorded"
          subtitle={report ? formatKes(report.totals.difference, "KES") : "…"}
        />
      </div>

      <ExpandableDataTable
        title="Payment reconciliation"
        subtitle="Your organization's deposits and withdrawals in this window, each checked against its payment. Problems are listed first."
        columns={columns}
        data={report?.lines || []}
        loading={loading}
        error={error}
        onRetry={load}
        emptyMessage="Nothing to reconcile in this window."
        exportFilename={`reconciliation-${startDate}-to-${endDate}`}
        exportColumns={[
          { accessorKey: "outcome", header: "outcome" },
          { accessorKey: "kind", header: "kind" },
          { accessorKey: "reference", header: "payment_reference" },
          { accessorKey: "paymentStatus", header: "payment_status" },
          { accessorKey: "status", header: "recorded_status" },
          { accessorKey: "paymentAmount", header: "payment_amount" },
          { accessorKey: "amount", header: "recorded_amount" },
          { accessorKey: "currency", header: "currency" },
          { accessorKey: "occurredAt", header: "occurred_at" },
          { accessorKey: "detail", header: "detail" },
        ]}
        getRowId={(row, i) => `${row.reference || "row"}-${i}`}
        renderDetail={(line) => renderLine(line)}
        headerActions={
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-9 rounded-sm border border-gray-200 bg-white px-2 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
            <span className="text-xs text-gray-500">to</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={today()}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-9 rounded-sm border border-gray-200 bg-white px-2 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
          </div>
        }
      />
    </div>
  );
}
